import React from 'react';
import { LIST_BEFORE_ASSIGNED_SHIP } from 'constants/enum';
import CustomMarker from 'components/Maps/CustomMarker';
import CustomMap from 'components/Maps/CustomMap';
import { addOffsetToPixel, fromLatLngToPixel, fromPixelToLatLng, getBearing, getDistance } from 'utils/MapUtils';
import {
  NOW_SHIP_ANIMATION_STEP_DURATION,
  NOW_SHIP_LOCK_AUTO_MOVE_MAP_DURATION,
  NOW_SHIP_MAX_ANIMATION_DISTANCE,
  NOW_SHIP_MAX_ANIMATION_STEP,
} from 'constants/constant';
import { isEmptyObject } from 'utils/utils';
import IconPick from 'assets/static/images/icon_pick.png';
import IconDrop from 'assets/static/images/icon_drop.png';
import PinDriver from 'assets/static/icons/icon_shipper.png';
import { EnumShipCustomerStatus } from '../../constants/enum';

const mapOptions = {
  mapTypeControlOptions: {
    position: 'BOTTOM_RIGHT', // this makes the map type control disappear
  },
  gestureHandling: 'greedy',
  disableDefaultUI: true,
  streetViewControl: false,
  fullscreenControl: false,
};

const PickMarker = React.memo(({ pickAddressInfo }) => (
  <CustomMarker
    position={{
      lat: pickAddressInfo.latitude,
      lng: pickAddressInfo.longitude,
    }}
    icon={IconPick}
    zIndex={1000}
  >
    <img src={IconPick} alt='icon' />
  </CustomMarker>
));

const DropMarker = ({ dropAddressInfo }) => (
  <CustomMarker
    position={{
      lat: dropAddressInfo.latitude,
      lng: dropAddressInfo.longitude,
    }}
    icon={IconDrop}
    zIndex={1000}
  >
    <img src={IconDrop} alt='icon' />
  </CustomMarker>
);

const DriverMarker = React.memo(({ driverLocation, tips }) => {
  if (isEmptyObject(driverLocation)) {
    return null;
  }
  return (
    <CustomMarker
      position={driverLocation}
      icon={PinDriver}
      rotation={driverLocation.heading}
      zIndex={2000}
      tips={tips}
    >
      <img src={PinDriver} alt='icon' />
    </CustomMarker>
  );
});

const MOVE = 1;
const ROTATE = 2;

// This class is mixed React and pure JS for implement smooth driver movement
export class ProcessingMap extends React.PureComponent {
  static getDerivedStateFromProps (props, state) {
    if (
      isEmptyObject(state.driverLocation) &&
      !isEmptyObject(props.driverLocation)
    ) {
      return {
        driverLocation: props.driverLocation,
      };
    }
    return null;
  }

  constructor (props) {
    super(props);

    const { driverLocation } = props;
    this.state = {
      googleMap: null,
      driverLocation,
    };
    this.queue = [];
    this.lockQueue = false;
    this.lockAutoMoveToDriver = false;
  }

  componentDidUpdate (prevProps) {
    this.handleUpdateDriverLocation(this.props, prevProps);
    this.handleZoomMapByStatus(this.props, prevProps);
    this.handleZoomMapByOffsetYMap(this.props, prevProps);
  }

  componentWillUnmount () {
    clearTimeout(this.zoomMapDidMountTimeout);
    clearTimeout(this.moveMapTimeout);
    clearTimeout(this.animateDriverTimeout);
    clearTimeout(this.unlockAutoMoveToDriverTimeout);
  }

  rotateDriver = (heading) => {
    this.setState(({ driverLocation }) => ({
      driverLocation: {
        ...driverLocation,
        heading,
      },
    }));
    this.lockQueue = false;
    this.processQueueJob();
  };

  moveDriver = (newLocation) => {
    const { driverLocation: currentLocation } = this.state;
    if (isEmptyObject(currentLocation)) {
      this.setState({
        driverLocation: newLocation,
      });
    } else if (
      getDistance(currentLocation, newLocation) >
      NOW_SHIP_MAX_ANIMATION_DISTANCE
    ) {
      this.setState(
        {
          driverLocation: newLocation,
        },
        () => {
          this.handleZoomMapByStatus(this.props);
          this.lockQueue = false;
          this.processQueueJob();
        },
      );
    } else {
      this.animateDriver(currentLocation, newLocation);
    }
  };

  animateDriver = (startLocation, endLocation) => {
    const recursiveMoveDriver = (step) => {
      const fraction = step / NOW_SHIP_MAX_ANIMATION_STEP;
      const animateLocation = {
        lat: fraction * endLocation.lat + (1 - fraction) * startLocation.lat,
        lng: fraction * endLocation.lng + (1 - fraction) * startLocation.lng,
      };
      const heading = getBearing(startLocation, animateLocation);
      if (
        !Number.isNaN(animateLocation.lat) &&
        !Number.isNaN(animateLocation.lng)
      ) {
        this.setState(
          {
            driverLocation: {
              ...animateLocation,
              heading,
            },
          },
          () => this.handleCheckDriverOutOfMap(animateLocation),
        );
      }
      if (step < NOW_SHIP_MAX_ANIMATION_STEP) {
        this.animateDriverTimeout = setTimeout(
          () => recursiveMoveDriver(step + 1),
          NOW_SHIP_ANIMATION_STEP_DURATION,
        );
      } else {
        this.lockQueue = false;
        this.processQueueJob();
      }
    };

    recursiveMoveDriver(0);
  };

  addToQueue = (driverLocation) => {
    const { lat, lng, heading } = driverLocation;
    this.queue.push({
      type: MOVE,
      lat,
      lng,
      heading,
    });
    this.processQueueJob();
  };

  processQueueJob = () => {
    if (!this.lockQueue && this.queue.length > 0) {
      this.lockQueue = true;
      const job = this.queue.shift();
      const { type } = job;
      switch (type) {
        case MOVE: {
          const { lat, lng, heading } = job;
          this.moveDriver({ lat, lng, heading });
          break;
        }
        case ROTATE: {
          const { heading } = job;
          this.rotateDriver(heading);
          break;
        }
        default:
          break;
      }
    }
  };

  handleSetGoogleMapRef = (googleMap) => {
    if (googleMap) {
      this.setState({ googleMap });
    }
  };

  handleMoveMap = (...rawLocations) => {
    const { googleMap } = this.state;
    // Guard: 如果地图未就绪, 延迟执行
    if (!googleMap) {
      this.moveMapTimeout = setTimeout(
        () => this.handleMoveMap(...rawLocations),
        1000,
      );
      return;
    }
    // 处理地图数据
    const { offsetYMap } = this.props;
    const { google } = window;
    const cleanedLocations = rawLocations.filter(Boolean); // 会有空数据
    // 地图点位只有一个时, 直接展示
    if (cleanedLocations.length === 1) {
      const firstLocation = cleanedLocations[0];
      const pixelatedLocation = fromLatLngToPixel(googleMap, new google.maps.LatLng(firstLocation));
      const transformedLocation = fromPixelToLatLng(googleMap, addOffsetToPixel(pixelatedLocation, {
        x: 0,
        y: offsetYMap / 2,
      }));
      googleMap.panTo(transformedLocation);
      return;
    }
    // 地图点位多于一个时, 计算边界并展示
    const bounds = new window.google.maps.LatLngBounds();
    cleanedLocations.forEach((location) => bounds.extend(location));
    googleMap.fitBounds(bounds, {
      bottom: offsetYMap + 15,
      left: 15,
      right: 15,
      top: 30,
    });
  };

  handleCheckDriverOutOfMap = (driverLocation) => {
    const { googleMap } = this.state;
    const isOutOfMap = !googleMap.getBounds().contains(driverLocation);
    if (!this.lockAutoMoveToDriver && isOutOfMap) {
      this.handleMoveMap(driverLocation);
    }
  };

  handleLockAutoMoveMapToDriver = () => {
    this.lockAutoMoveToDriver = true;
    clearTimeout(this.unlockAutoMoveToDriverTimeout);
    this.unlockAutoMoveToDriverTimeout = setTimeout(() => {
      this.lockAutoMoveToDriver = false;
    }, NOW_SHIP_LOCK_AUTO_MOVE_MAP_DURATION);
  };

  handleUpdateDriverLocation = (props, prevProps) => {
    const { driverLocation } = props;
    const { driverLocation: oldDriverLocation } = prevProps;
    // IT IS VERY IMPORTANT, BECAUSE IT WILL CAUSE BUG CRASH THE BROWSER
    // GOOGLE MAP OVERLAY VIEW CAN'T DRAW NaN LOCATION
    // hack here, compare value of object
    if (JSON.stringify(driverLocation) !== JSON.stringify(oldDriverLocation)) {
      this.addToQueue(driverLocation);
    }
  };

  handleZoomMapByStatus = (props, prevProps = {}) => {
    const { status } = props;
    const { status: oldStatus } = prevProps;
    if (oldStatus !== status) {
      const locations = props.onZoomMapByStatus(status);
      this.handleMoveMap(...locations);
    }
  };

  handleZoomMapByOffsetYMap = (props, prevProps = {}) => {
    const { offsetYMap } = props;
    const { offsetYMap: oldOffsetYMap } = prevProps;
    if (oldOffsetYMap !== offsetYMap) {
      this.handleZoomMapByStatus(props);
    }
  };

  render () {
    const {
      data,
      status,
      pickAddressInfo,
      dropAddressInfo,
      isNowShipService,
    } = this.props;

    const { driverLocation } = this.state;
    const isShowDriver = !LIST_BEFORE_ASSIGNED_SHIP.includes(status) && !!data?.enroute_status && status !== EnumShipCustomerStatus.COMPLETED
    return (
      <div className='order-processing-map' style={{ width: '100%', height: '100%' }}>
        <CustomMap
          defaultZoom={16}
          height='100%'
          clickableIcons={false}
          options={mapOptions}
          onDrag={this.handleLockAutoMoveMapToDriver}
          onZoomChanged={this.handleLockAutoMoveMapToDriver}
          onMapLoaded={this.handleSetGoogleMapRef}
        >
          <PickMarker
            status={status}
            pickAddressInfo={pickAddressInfo}
            isNowShipService={isNowShipService}
          />
          <DropMarker
            status={status}
            dropAddressInfo={dropAddressInfo}
            isNowShipService={isNowShipService}
          />
          {isShowDriver && (
            <DriverMarker
              status={status}
              driverLocation={driverLocation}
              // 2024.11.15 先不展示 tips
              // tips={strategy?.driver}
            />
          )}
        </CustomMap>
      </div>
    );
  }
}
